import { useMemo, useState } from "react";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import AnalyticsTeacherForecastSchoolsModal from "@/components/AnalyticsTeacherForecastSchoolsModal";
import { buildDataStatusChip } from "@/utils/dataFreshness";
import "../pages/AnalyticsTeacherForecastTab.css";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");

const fmtK = (v) => {
  if (typeof v !== "number") return v ?? "—";
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const AGE_BUCKETS = ["<30", "30-40", "40-50", "50-55", "55+"];
const RETIREMENT_YEAR_WEIGHTS = [0.146, 0.162, 0.187, 0.233, 0.272];

const priorityLabel = (rank) => (rank <= 3 ? { label: "Critical", className: "atf-red" } : { label: "High", className: "" });

const AnalyticsTeacherForecastTab = ({
  teacherData,
  loading = false,
  onRefresh,
  onGenerate,
  scopeLabel = "Pune District",
}) => {
  const [teacherMetricModal, setTeacherMetricModal] = useState(null);
  const summary = teacherData?.summary;
  const ageDistribution = useMemo(() => teacherData?.age_distribution || {}, [teacherData?.age_distribution]);
  const blocks = useMemo(() => teacherData?.block_forecast || [], [teacherData?.block_forecast]);

  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => (b.forecast_shortage_5yr ?? 0) - (a.forecast_shortage_5yr ?? 0)),
    [blocks],
  );

  const top5 = useMemo(() => sortedBlocks.slice(0, 5), [sortedBlocks]);

  const coveragePct = useMemo(() => {
    if (typeof summary?.supply_coverage_pct === "number") {
      return summary.supply_coverage_pct;
    }
    const retiring = summary?.retiring_in_5_years || 0;
    const entrants = summary?.new_entrants || 0;
    if (!retiring) return entrants > 0 ? 100 : 0;
    return Math.min(100, Math.round((entrants / retiring) * 1000) / 10);
  }, [summary]);

  const pipelineSurplus = summary?.pipeline_surplus_5yr || 0;
  const netShortage = summary?.net_shortage_5yr || 0;
  const hasPipelineSurplus = pipelineSurplus > 0;

  const retirementPct = useMemo(() => {
    const total = summary?.total_teachers || 0;
    if (!total) return 0;
    return Math.round(((summary?.retiring_in_5_years || 0) / total) * 1000) / 10;
  }, [summary]);

  const ctetCoveragePct = useMemo(
    () => summary?.ctet_coverage_pct ?? summary?.avg_ctet_rate ?? 0,
    [summary],
  );

  const ctetQualifiedCount = summary?.ctet_qualified_count ?? 0;

  const gaugeStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--atf-purple) 0 ${coveragePct}%, rgba(139,92,246,.13) ${coveragePct}% 100%)`,
    }),
    [coveragePct],
  );

  const ageBars = useMemo(() => {
    const counts = AGE_BUCKETS.map((key) => ageDistribution[key] || 0);
    const max = Math.max(...counts, 1);
    const maxHeight = 239;
    return AGE_BUCKETS.map((key, index) => ({
      key,
      count: counts[index],
      height: Math.max(10, Math.round((counts[index] / max) * maxHeight)),
    }));
  }, [ageDistribution]);

  const retirementWave = useMemo(() => {
    const total = summary?.retiring_in_5_years || 0;
    const values = RETIREMENT_YEAR_WEIGHTS.map((w) => Math.round(total * w));
    const maxVal = Math.max(...values, 1);
    return values.map((value, index) => ({
      year: index + 1,
      value,
      widthPct: Math.round((value / maxVal) * 100),
    }));
  }, [summary]);

  const planningBlocks = useMemo(
    () => top5.slice(0, 5).map((b) => b.block).join(", "),
    [top5],
  );

  const criticalNames = useMemo(
    () => top5.slice(0, 3).map((b) => b.block).join(", "),
    [top5],
  );

  const defaultRoot = useMemo(
    () => [
      `${fmt(summary?.retiring_in_5_years)} teachers are projected to retire in 5 years (${retirementPct}% of workforce).`,
      `Net shortage forecast is ${fmt(netShortage)} teachers with ${coveragePct}% pipeline coverage.`,
      `Critical blocks ${criticalNames || "need review"} combine high retirements with large forecast shortage.`,
    ],
    [summary, retirementPct, netShortage, coveragePct, criticalNames],
  );

  const defaultRecs = useMemo(
    () => [
      `Start staffing review for ${criticalNames || "priority blocks"} in the first planning cycle.`,
      "Maintain substitute buffer pool for blocks with high 3-year retirement counts.",
      `Use ${ctetCoveragePct}% CTET coverage (${fmt(ctetQualifiedCount)} qualified) to focus on deployment balance and subject-mix planning.`,
    ],
    [criticalNames, ctetCoveragePct, ctetQualifiedCount],
  );

  const defaultActions = useMemo(
    () => [
      `Assign block officers to ${planningBlocks || "priority blocks"} for vacancy tracking within 2 weeks.`,
      hasPipelineSurplus
        ? `Shift focus to deployment balance across ${fmt(pipelineSurplus)} surplus teachers.`
        : `Accelerate hiring pipeline until the ${fmt(netShortage)} teacher gap drops below 500.`,
      "Review workforce plan monthly until retirement risk stabilizes in critical blocks.",
    ],
    [planningBlocks, hasPipelineSurplus, pipelineSurplus, netShortage],
  );

  if (loading && !teacherData) {
    return (
      <div className="analytics-teacher-view" data-testid="analytics-teacher-tab">
        <div className="atf-loading">
          <div className="atf-loading-spinner" />
          <span>Generating teacher workforce forecast…</span>
        </div>
      </div>
    );
  }

  if (!teacherData) {
    return (
      <div className="analytics-teacher-view" data-testid="analytics-teacher-tab">
        <div className="atf-loading">
          <p style={{ margin: 0, fontSize: 18, color: "var(--atf-text)", fontWeight: 900 }}>Teacher Forecast</p>
          <p style={{ margin: 0, maxWidth: 420, textAlign: "center" }}>
            AI-powered retirement forecast and hiring needs analysis.
          </p>
          <button type="button" className="atf-btn atf-btn-primary" onClick={onGenerate}>
            Generate Forecast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-teacher-view" data-testid="analytics-teacher-tab">
      <section className="atf-page-header">
        <div>
          <div className="atf-eyebrow">
            <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
            </svg>
            AI-powered workforce forecast · {scopeLabel} · 2025–26
          </div>
          <h1>Teacher Forecast</h1>
          <p>
            Predict teacher retirement risk, hiring gap, age distribution and block-wise staffing pressure in one clear
            decision layer.
          </p>
        </div>
        <div className="atf-header-actions">
          <button type="button" className="atf-btn" title="Export">
            <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </button>
          <button type="button" className="atf-btn atf-btn-primary" onClick={onRefresh} disabled={loading}>
            <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <section className="atf-hero-card">
        <div className="atf-hero-layout">
          <div>
            <div className="atf-readiness-main">
              <div className="atf-gauge" style={gaugeStyle} aria-label={`Teacher workforce coverage ${coveragePct} percent`}>
                <div>
                  <strong>{coveragePct}%</strong>
                  <span>Coverage</span>
                </div>
              </div>
              <div className="atf-readiness-copy">
                <h2 className="dashboard-kpi-hero-title">Teacher Workforce Command Centre</h2>
                <p>
                  Retirement pressure is {retirementPct < 12 ? "manageable" : "elevated"}
                  {hasPipelineSurplus
                    ? `, and the young-teacher pipeline exceeds projected retirements by ${fmt(pipelineSurplus)} teachers.`
                    : `, but a ${fmt(netShortage)} teacher 5-year net shortage needs proactive recruitment, transfer planning and substitute pool creation.`}
                </p>
                <div className="atf-status-row">
                  {hasPipelineSurplus ? (
                    <HeroStatusChip chipClass="atf-status-chip" variant="atf-green" showChevron={false}>
                      {fmt(pipelineSurplus)} Pipeline Surplus
                    </HeroStatusChip>
                  ) : netShortage > 0 ? (
                    <HeroStatusChip
                      chipClass="atf-status-chip"
                      variant="atf-red"
                      onClick={() => setTeacherMetricModal("net_shortage")}
                      ariaLabel={`${fmt(netShortage)} net shortage — view schools facing workforce gap`}
                    >
                      {fmt(netShortage)} Net Shortage
                    </HeroStatusChip>
                  ) : (
                    <HeroStatusChip chipClass="atf-status-chip" variant="atf-red" showChevron={false}>
                      {fmt(netShortage)} Net Shortage
                    </HeroStatusChip>
                  )}
                  {(summary?.retiring_in_5_years ?? 0) > 0 ? (
                    <HeroStatusChip
                      chipClass="atf-status-chip"
                      variant="atf-orange"
                      onClick={() => setTeacherMetricModal("retiring_5_years")}
                      ariaLabel={`${fmt(summary?.retiring_in_5_years)} teachers retiring in 5 years — view school list`}
                    >
                      {fmt(summary?.retiring_in_5_years)} Retiring in 5 Years
                    </HeroStatusChip>
                  ) : (
                    <HeroStatusChip chipClass="atf-status-chip" variant="atf-orange" showChevron={false}>
                      {fmt(summary?.retiring_in_5_years)} Retiring in 5 Years
                    </HeroStatusChip>
                  )}
                  <HeroStatusChip chipClass="atf-status-chip" variant="atf-green" showChevron={false}>
                    {ctetCoveragePct}% CTET · {fmt(ctetQualifiedCount)} qualified
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="atf-status-chip" variant="atf-blue" showChevron={false}>
                    {buildDataStatusChip(
                      { total_schools: summary?.total_schools, total_teachers: summary?.total_teachers },
                      teacherData?.generated_at
                    )}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="atf-summary-note">
              <div className="atf-icon-box atf-purple">
                <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                  <circle cx="12" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                </svg>
              </div>
              <div>
                <b>Primary planning action</b>
                <span>
                  Start targeted hiring for {planningBlocks || "priority blocks"} before the next academic staffing
                  cycle.
                </span>
              </div>
            </div>
          </div>

          <div className="atf-metric-grid" aria-label="Teacher forecast KPIs">
            <article className="atf-metric-card">
              <small>Total Teachers</small>
              <strong>{fmt(summary?.total_teachers)}</strong>
              <span>Current workforce base</span>
            </article>
            <article
              className="atf-metric-card atf-orange clickable"
              role="button"
              tabIndex={0}
              aria-label="Retiring in 5 Years: view schools with retirement exposure"
              onClick={() => setTeacherMetricModal("retiring_5_years")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setTeacherMetricModal("retiring_5_years");
                }
              }}
            >
              <small>Retiring in 5 Years</small>
              <strong>{fmt(summary?.retiring_in_5_years)}</strong>
              <span className="atf-warn">{retirementPct}% of workforce</span>
            </article>
            <article className="atf-metric-card atf-green">
              <small>New Entrants</small>
              <strong>{fmt(summary?.new_entrants)}</strong>
              <span>Expected supply pipeline</span>
            </article>
            <article
              className={`atf-metric-card ${hasPipelineSurplus ? "atf-green" : "atf-red"}${!hasPipelineSurplus ? " clickable" : ""}`}
              role={!hasPipelineSurplus ? "button" : undefined}
              tabIndex={!hasPipelineSurplus ? 0 : undefined}
              aria-label={
                !hasPipelineSurplus ? "Net Shortage: view schools facing workforce gap" : "Pipeline surplus — no shortage drill-down"
              }
              onClick={!hasPipelineSurplus ? () => setTeacherMetricModal("net_shortage") : undefined}
              onKeyDown={
                !hasPipelineSurplus
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setTeacherMetricModal("net_shortage");
                      }
                    }
                  : undefined
              }
            >
              <small>{hasPipelineSurplus ? "Pipeline Surplus" : "Net Shortage"}</small>
              <strong>{fmt(hasPipelineSurplus ? pipelineSurplus : netShortage)}</strong>
              <span className={hasPipelineSurplus ? "" : "atf-risk"}>
                {hasPipelineSurplus ? "Supply exceeds demand" : "Requires intervention"}
              </span>
            </article>
          </div>
        </div>
      </section>

      <div className="atf-section-title" id="snapshot">
        <div>
          <h2>Workforce Snapshot</h2>
          <p>
            The first layer summarizes supply, retirement exposure, qualification strength and hiring pressure without
            forcing users to read long AI text.
          </p>
        </div>
        <button type="button" className="atf-section-action" onClick={() => document.getElementById("actions")?.scrollIntoView({ behavior: "smooth" })}>
          View action queue →
        </button>
      </div>

      <section className="atf-snapshot-grid">
        <article className="atf-snapshot-card">
          <div className="atf-snapshot-head">
            <div className="atf-icon-box">
              <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
              </svg>
            </div>
            <span className="atf-status-dot" />
          </div>
          <h3>Teacher Base</h3>
          <div className="atf-snapshot-score">
            <strong>{fmtK(summary?.total_teachers)}</strong>
            <span>Teachers</span>
          </div>
          <div className="atf-progress-line">
            <i style={{ width: "92%" }} />
          </div>
          <div className="atf-snapshot-meta">
            <span>Stable base</span>
            <span>District-wide</span>
          </div>
        </article>

        <article
          className="atf-snapshot-card clickable"
          role="button"
          tabIndex={0}
          aria-label="Retirement Risk: view schools with retirement exposure"
          onClick={() => setTeacherMetricModal("retiring_5_years")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setTeacherMetricModal("retiring_5_years");
            }
          }}
        >
          <div className="atf-snapshot-head">
            <div className="atf-icon-box atf-orange">
              <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <span className="atf-status-dot atf-amber" />
          </div>
          <h3>Retirement Risk</h3>
          <div className="atf-snapshot-score">
            <strong>{retirementPct}%</strong>
            <span>5-year</span>
          </div>
          <div className="atf-progress-line">
            <i style={{ width: `${Math.min(100, retirementPct * 7)}%`, background: "var(--atf-secondary)" }} />
          </div>
          <div className="atf-snapshot-meta">
            <span>{fmt(summary?.retiring_in_5_years)} exits</span>
            <span>Watch list</span>
          </div>
        </article>

        <article
          className={`atf-snapshot-card${!hasPipelineSurplus && netShortage > 0 ? " clickable" : ""}`}
          role={!hasPipelineSurplus && netShortage > 0 ? "button" : undefined}
          tabIndex={!hasPipelineSurplus && netShortage > 0 ? 0 : undefined}
          aria-label={
            !hasPipelineSurplus && netShortage > 0
              ? "Hiring Gap: view schools facing workforce gap"
              : undefined
          }
          onClick={!hasPipelineSurplus && netShortage > 0 ? () => setTeacherMetricModal("net_shortage") : undefined}
          onKeyDown={
            !hasPipelineSurplus && netShortage > 0
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setTeacherMetricModal("net_shortage");
                  }
                }
              : undefined
          }
        >
          <div className="atf-snapshot-head">
            <div className={`atf-icon-box ${hasPipelineSurplus ? "atf-green" : "atf-red"}`}>
              <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <span className={`atf-status-dot ${hasPipelineSurplus ? "" : "atf-red"}`} />
          </div>
          <h3>{hasPipelineSurplus ? "Pipeline Surplus" : "Hiring Gap"}</h3>
          <div className="atf-snapshot-score">
            <strong>{fmt(hasPipelineSurplus ? pipelineSurplus : netShortage)}</strong>
            <span>{hasPipelineSurplus ? "Surplus" : "Shortage"}</span>
          </div>
          <div className="atf-progress-line">
            <i
              style={{
                width: `${hasPipelineSurplus ? 100 : Math.min(100, netShortage / 25)}%`,
                background: hasPipelineSurplus ? "var(--atf-accent)" : "var(--atf-danger)",
              }}
            />
          </div>
          <div className="atf-snapshot-meta">
            <span>{hasPipelineSurplus ? "Supply exceeds demand" : "Uncovered demand"}</span>
            <span>{hasPipelineSurplus ? "Monitor transfers" : "High priority"}</span>
          </div>
        </article>

        <article className="atf-snapshot-card">
          <div className="atf-snapshot-head">
            <div className="atf-icon-box atf-green">
              <svg className="atf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" />
                <path d="M9 14.5 7 22l5-3 5 3-2-7.5" />
              </svg>
            </div>
            <span className="atf-status-dot" />
          </div>
          <h3>CTET Coverage</h3>
          <div className="atf-snapshot-score">
            <strong>{ctetCoveragePct}%</strong>
            <span>Qualified</span>
          </div>
          <div className="atf-progress-line">
            <i style={{ width: `${Math.max(ctetCoveragePct, 2)}%`, background: "var(--atf-accent)" }} />
          </div>
          <div className="atf-snapshot-meta">
            <span>{fmt(ctetQualifiedCount)} CTET qualified</span>
            <span>{ctetCoveragePct >= 50 ? "On track" : ctetCoveragePct >= 20 ? "Monitor" : "Critical gap"}</span>
          </div>
        </article>
      </section>

      <div className="atf-section-title" id="diagnostics">
        <div>
          <h2>Age and Retirement Diagnostics</h2>
          <p>
            Age distribution and 5-year retirement wave are separated from KPIs so the forecast is easier to interpret
            and act on.
          </p>
        </div>
        <button type="button" className="atf-section-action" onClick={() => document.getElementById("blocks")?.scrollIntoView({ behavior: "smooth" })}>
          Go to block risks →
        </button>
      </div>

      <section className="atf-analytics-grid">
        <article className="atf-panel">
          <div className="atf-panel-head">
            <div>
              <h3>Age Distribution</h3>
              <p>Most teachers are in the 40–50 group, while the 55+ group creates near-term replacement pressure.</p>
            </div>
            <span className="atf-pill atf-purple">Teachers</span>
          </div>
          <div className="atf-age-chart" aria-label="Age distribution bar chart">
            <div className="atf-gridlines" />
            <div className="atf-bar-chart">
              {ageBars.map((bar) => (
                <div className="atf-bar-item" key={bar.key}>
                  <div className="atf-vbar" style={{ height: `${bar.height}px` }}>
                    <span>{fmt(bar.count)}</span>
                  </div>
                  {bar.key}
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="atf-panel">
          <div className="atf-panel-head">
            <div>
              <h3>5-Year Retirement Wave</h3>
              <p>
                Retirements accelerate toward year 4 and year 5. Planning should begin before vacancies become
                operational shortages.
              </p>
            </div>
            <span className="atf-pill atf-red">Forecast</span>
          </div>
          <div className="atf-forecast-rows" aria-label="Retirement wave horizontal bar chart">
            {retirementWave.map((row) => (
              <div className="atf-forecast-row" key={row.year}>
                <div className="atf-forecast-label">Year {row.year}</div>
                <div className="atf-forecast-track">
                  <div className="atf-forecast-bar" style={{ width: `${row.widthPct}%` }} />
                </div>
                <div className="atf-forecast-value">{fmt(row.value)}</div>
              </div>
            ))}
          </div>
          <div className="atf-bridge-card">
            <div className="atf-bridge-top">
              <b>Supply coverage against retirement demand</b>
              <strong>{coveragePct}%</strong>
            </div>
            <div className="atf-bridge-line">
              <i style={{ width: `${coveragePct}%` }} />
            </div>
            <p className="atf-bridge-caption">
              {hasPipelineSurplus
                ? `New entrants (${fmt(summary?.new_entrants)}) exceed projected retirements (${fmt(summary?.retiring_in_5_years)}) by ${fmt(pipelineSurplus)} teachers — pipeline fully covered.`
                : `New entrants cover ${fmt(summary?.new_entrants)} of ${fmt(summary?.retiring_in_5_years)} projected retirements. Remaining gap: ${fmt(netShortage)} teachers.`}
            </p>
          </div>
        </article>
      </section>

      <div className="atf-section-title" id="blocks">
        <div>
          <h2>Block-Wise Shortage Risk</h2>
          <p>
            Blocks are ranked by combined retirement share and net shortage, helping officers prioritize transfers,
            recruitment and vacancy buffers.
          </p>
        </div>
        <button type="button" className="atf-section-action" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Back to top ↑
        </button>
      </div>

      <section className="atf-two-column" id="actions">
        <article className="atf-panel atf-table-card">
          <div className="atf-table-head">
            <div>
              <h3>Priority Blocks for Staffing Action</h3>
              <p>Top blocks requiring hiring or redeployment planning.</p>
            </div>
            <span className="atf-pill atf-red">Top {top5.length}</span>
          </div>
          <table className="atf-table" aria-label="Teacher forecast block risk table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Teachers</th>
                <th>Retiring 5Y</th>
                <th>Retirement %</th>
                <th>Net Shortage</th>
                <th>CTET</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((block, index) => {
                const tag = priorityLabel(index + 1);
                return (
                  <tr key={block.block}>
                    <td>{index + 1}</td>
                    <td>{block.block}</td>
                    <td>{fmt(block.total_teachers)}</td>
                    <td className="atf-score">{fmt(block.retiring_in_5_years)}</td>
                    <td>{block.retirement_risk_pct}%</td>
                    <td>{block.forecast_shortage_5yr}</td>
                    <td>{block.ctet_rate}%</td>
                    <td>
                      <span className={`atf-tag ${tag.className}`}>{tag.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </article>

        <InterventionActionPlan
          aiContent={teacherData?.ai_insights}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <AnalyticsTeacherForecastSchoolsModal
        open={Boolean(teacherMetricModal)}
        metric={teacherMetricModal}
        onClose={() => setTeacherMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={teacherMetricModal === "net_shortage" ? netShortage : retirementPct}
      />
    </div>
  );
};

export default AnalyticsTeacherForecastTab;
